import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/dist/handsontable.full.css';
import { useQuery } from '@tanstack/react-query';
import ExcelJS from 'exceljs';
import { Check, GitCommitHorizontal, History, Loader2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projectDataClient } from '../../../../lib/connect/clients';
import { parseExcelBuffer } from './parseExcel';
import { WorkbookVersionHistory } from './WorkbookVersionHistory';
// Register Handsontable modules once at module level.
registerAllModules();
/**
 * WorkbookEditor — XLSX-native spreadsheet editor.
 *
 * Flow:
 *   1. LoadWorkbook RPC → XLSX bytes from MinIO
 *   2. ExcelJS parses in browser → Handsontable renders with full formatting
 *   3. Edits happen in Handsontable (autosave debounced)
 *   4. Save: ExcelJS serializes → SaveWorkbook RPC → MinIO
 *   5. Commit: explicit version snapshot with message
 */ export function WorkbookEditor({ projectId, volumeId }) {
    const [params, setParams] = useSearchParams();
    const requestedSheet = params.get('workbookSheet');
    const [saveStatus, setSaveStatus] = useState('idle');
    const [showCommit, setShowCommit] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [commitMessage, setCommitMessage] = useState('');
    const [committing, setCommitting] = useState(false);
    const debounceRef = useRef(null);
    const hotRef = useRef(null);
    const sheetsRef = useRef([]);
    // Load the XLSX from the backend.
    const { data: workbookData, isLoading, error } = useQuery({
        queryKey: [
            'workbook',
            projectId,
            volumeId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.loadWorkbook({
                projectId,
                volumeId,
                version: 0
            });
            if (!resp.xlsxData || resp.xlsxData.length === 0) return null;
            // Convert proto Uint8Array to a clean ArrayBuffer for ExcelJS.
            const xlsxBytes = new Uint8Array(resp.xlsxData);
            const buffer = xlsxBytes.buffer.slice(xlsxBytes.byteOffset, xlsxBytes.byteOffset + xlsxBytes.byteLength);
            console.log('[WorkbookEditor] Parsing XLSX', {
                bytes: xlsxBytes.length,
                version: resp.version
            });
            const sheets = await parseExcelBuffer(buffer);
            console.log('[WorkbookEditor] Parsed', {
                sheets: sheets.length,
                firstSheetRows: sheets[0]?.data?.length
            });
            sheetsRef.current = sheets;
            return {
                sheets,
                version: resp.version,
                contentHash: resp.contentHash
            };
        },
        enabled: !!projectId && !!volumeId,
        staleTime: 60000
    });
    const sheets = workbookData?.sheets ?? [];
    const activeSheetIdx = requestedSheet === null ? 0 : sheets.findIndex((sheet)=>sheet.name === requestedSheet);
    const activeSheet = sheets[activeSheetIdx] ?? null;
    // Serialize current Handsontable state back to XLSX bytes.
    const serializeToXlsx = useCallback(async ()=>{
        // Update the active sheet's data from HotTable before serializing.
        const hot = hotRef.current?.hotInstance;
        if (hot && sheetsRef.current[activeSheetIdx]) {
            sheetsRef.current[activeSheetIdx].data = hot.getData();
        }
        const workbook = new ExcelJS.Workbook();
        for (const sheet of sheetsRef.current){
            const ws = workbook.addWorksheet(sheet.name);
            // Write cell data.
            for(let r = 0; r < sheet.data.length; r++){
                const row = ws.getRow(r + 1);
                for(let c = 0; c < sheet.data[r].length; c++){
                    const val = sheet.data[r][c];
                    if (val !== null && val !== undefined && val !== '') {
                        row.getCell(c + 1).value = val;
                    }
                }
                row.commit();
            }
            // Restore merged cells.
            for (const mc of sheet.mergeCells){
                ws.mergeCells(mc.row + 1, mc.col + 1, mc.row + mc.rowspan, mc.col + mc.colspan);
            }
            // Restore column widths (px → Excel character units).
            for(let c = 0; c < sheet.colWidths.length; c++){
                const col = ws.getColumn(c + 1);
                col.width = Math.max(1, Math.round(sheet.colWidths[c] / 7));
            }
            // Restore frozen panes.
            if (sheet.frozenRows > 0 || sheet.frozenCols > 0) {
                ws.views = [
                    {
                        state: 'frozen',
                        xSplit: sheet.frozenCols,
                        ySplit: sheet.frozenRows,
                        topLeftCell: undefined,
                        activeCell: 'A1'
                    }
                ];
            }
        }
        const buffer = await workbook.xlsx.writeBuffer();
        return new Uint8Array(buffer);
    }, [
        activeSheetIdx
    ]);
    // Save to backend (autosave).
    const saveWorkbook = useCallback(async ()=>{
        setSaveStatus('saving');
        try {
            const xlsxBytes = await serializeToXlsx();
            await projectDataClient.saveWorkbook({
                projectId,
                volumeId,
                xlsxData: xlsxBytes
            });
            setSaveStatus('saved');
            setTimeout(()=>setSaveStatus('idle'), 2000);
        } catch (err) {
            console.error('Failed to save workbook:', err);
            setSaveStatus('error');
        }
    }, [
        projectId,
        volumeId,
        serializeToXlsx
    ]);
    // Debounced save on cell change.
    const handleAfterChange = useCallback((changes, source)=>{
        if (!changes || source === 'loadData') return;
        // Update sheetsRef with live data.
        const hot = hotRef.current?.hotInstance;
        if (hot && sheetsRef.current[activeSheetIdx]) {
            sheetsRef.current[activeSheetIdx].data = hot.getData();
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(()=>saveWorkbook(), 2000);
    }, [
        activeSheetIdx,
        saveWorkbook
    ]);
    // Commit — explicit version with message.
    const handleCommit = async ()=>{
        setCommitting(true);
        try {
            const xlsxBytes = await serializeToXlsx();
            await projectDataClient.saveWorkbook({
                projectId,
                volumeId,
                xlsxData: xlsxBytes,
                message: commitMessage.trim()
            });
            setShowCommit(false);
            setCommitMessage('');
            setSaveStatus('saved');
            setTimeout(()=>setSaveStatus('idle'), 2000);
        } catch (err) {
            console.error('Failed to commit:', err);
        } finally{
            setCommitting(false);
        }
    };
    // Switch sheets — save current data to sheetsRef first.
    const switchSheet = (idx)=>{
        const hot = hotRef.current?.hotInstance;
        if (hot && sheetsRef.current[activeSheetIdx]) {
            sheetsRef.current[activeSheetIdx].data = hot.getData();
        }
        setParams((previous)=>{
            const next = new URLSearchParams(previous);
            next.set('workbookSheet', sheets[idx].name);
            return next;
        }, {
            replace: true
        });
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex h-64 items-center justify-center",
            children: [
                /*#__PURE__*/ _jsx(Loader2, {
                    className: "h-5 w-5 animate-spin text-slate-400"
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "ml-2 text-sm text-slate-500",
                    children: "Loading spreadsheet..."
                })
            ]
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex h-64 flex-col items-center justify-center text-sm text-rose-600",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: "Failed to load workbook"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-1 text-xs text-slate-500",
                    children: error instanceof Error ? error.message : 'Unknown error'
                })
            ]
        });
    }
    if (!workbookData) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-64 items-center justify-center text-sm text-slate-500",
            children: "No workbook data yet. Load a template above to get started."
        });
    }
    if (requestedSheet !== null && !activeSheet) {
        return /*#__PURE__*/ _jsxs("p", {
            role: "alert",
            children: [
                "The requested workbook sheet “",
                requestedSheet,
                "” is unavailable."
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-xl border border-slate-200 bg-white shadow-sm",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center border-b border-slate-200 bg-slate-50 px-1 overflow-x-auto",
                children: [
                    sheets.map((sheet, idx)=>/*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: ()=>switchSheet(idx),
                            className: `flex-shrink-0 border-b-2 px-3 py-2 text-[12px] font-medium transition-colors ${idx === activeSheetIdx ? 'border-slate-900 text-slate-900 bg-white' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-white/50'}`,
                            children: sheet.name
                        }, sheet.name)),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "ml-auto flex flex-shrink-0 items-center gap-1.5 px-2",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setShowHistory(true),
                                className: "rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600",
                                title: "Version history",
                                children: /*#__PURE__*/ _jsx(History, {
                                    className: "h-3.5 w-3.5"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setShowCommit(true),
                                className: "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50",
                                title: "Commit version",
                                children: [
                                    /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                        className: "h-3 w-3"
                                    }),
                                    "Commit"
                                ]
                            }),
                            saveStatus === 'saving' && /*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-1 text-[11px] text-slate-400",
                                children: [
                                    /*#__PURE__*/ _jsx(Loader2, {
                                        className: "h-3 w-3 animate-spin"
                                    }),
                                    " Saving..."
                                ]
                            }),
                            saveStatus === 'saved' && /*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-1 text-[11px] text-emerald-600",
                                children: [
                                    /*#__PURE__*/ _jsx(Check, {
                                        className: "h-3 w-3"
                                    }),
                                    " Saved"
                                ]
                            }),
                            saveStatus === 'error' && /*#__PURE__*/ _jsx("span", {
                                className: "text-[11px] text-rose-600",
                                children: "Save failed"
                            })
                        ]
                    })
                ]
            }),
            activeSheet && /*#__PURE__*/ _jsx(SheetGrid, {
                hotRef: hotRef,
                sheet: activeSheet,
                onAfterChange: handleAfterChange
            }, activeSheetIdx),
            /*#__PURE__*/ _jsx(WorkbookVersionHistory, {
                isOpen: showHistory,
                onClose: ()=>setShowHistory(false),
                projectId: projectId,
                volumeId: volumeId
            }),
            showCommit && /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-50 flex items-center justify-center bg-black/40",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                    className: "h-5 w-5 text-emerald-600"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-sm font-semibold text-slate-900",
                                    children: "Commit workbook version"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "mt-1.5 text-xs text-slate-500",
                            children: "Create a snapshot of the current spreadsheet. Previous versions can be viewed and restored."
                        }),
                        /*#__PURE__*/ _jsx("textarea", {
                            value: commitMessage,
                            onChange: (e)=>setCommitMessage(e.target.value),
                            placeholder: "What changed? (optional)",
                            className: "mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-100",
                            rows: 2,
                            ref: (el)=>el?.focus()
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-3 flex items-center justify-end gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: ()=>setShowCommit(false),
                                    className: "rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100",
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    onClick: handleCommit,
                                    disabled: committing,
                                    className: "inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50",
                                    children: [
                                        committing ? /*#__PURE__*/ _jsx(Loader2, {
                                            className: "h-3.5 w-3.5 animate-spin"
                                        }) : /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        "Commit"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// SheetGrid — isolated HotTable render
// ---------------------------------------------------------------------------
function SheetGrid({ hotRef, sheet, onAfterChange }) {
    // Deep-copy the data array so Handsontable can mutate it freely.
    const [data] = useState(()=>sheet.data.map((row)=>[
                ...row
            ]));
    console.log('[SheetGrid] Rendering', {
        name: sheet.name,
        rows: data.length,
        cols: data[0]?.length,
        merges: sheet.mergeCells.length,
        frozenRows: sheet.frozenRows
    });
    return /*#__PURE__*/ _jsx("div", {
        id: "hot-container",
        style: {
            height: '600px',
            width: '100%',
            overflow: 'hidden',
            position: 'relative'
        },
        children: /*#__PURE__*/ _jsx(HotTable, {
            ref: hotRef,
            data: data,
            colHeaders: true,
            rowHeaders: true,
            width: "100%",
            height: 600,
            licenseKey: "non-commercial-and-evaluation",
            afterChange: onAfterChange,
            contextMenu: true,
            manualColumnResize: true,
            manualRowResize: true
        })
    });
}
